import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, OnDestroy, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, interval, debounceTime, timer, merge } from 'rxjs';
interface AgGridApi {
  setHorizontalScrollPosition(position: number): void;
  getAllDisplayedColumns(): AgColumn[];
  addEventListener(eventName: string, listener: (event: any) => void): void;
  removeEventListener(eventName: string, listener: (event: any) => void): void;
}
interface AgColumn {
  getActualWidth(): number;
}
interface ScrollEvent {
  direction: 'horizontal' | 'vertical';
  left: number;
}
/**
 * Unified directive that adds synchronized scrollbars to AG Grid tables
 * Supports both top and bottom scrollbars for complete horizontal scrolling capability
 */
@Directive({ selector: '[miqAgScrollbar]' })
export class AgScrollbarDirective implements AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly updateScrollbar$ = new Subject<void>();
  private readonly initializationAttempt$ = new Subject<void>();
  private readonly resetScrollPosition$ = new Subject<void>();
  private gridApi: AgGridApi | null = null;
  private topScrollbarElement: HTMLElement | null = null;
  private bottomScrollbarElement: HTMLElement | null = null;
  private topScrollbarContent: HTMLElement | null = null;
  private bottomScrollbarContent: HTMLElement | null = null;
  private isSynchronizingScroll = false;
  private monitoringInterval: number | null = null;
  // Store event listener references for proper cleanup
  private topScrollbarScrollHandler: ((event: Event) => void) | null = null;
  private bottomScrollbarScrollHandler: ((event: Event) => void) | null = null;
  private gridScrollHandler: ((event: ScrollEvent) => void) | null = null;
  private readonly gridEventHandlers: Map<string, (event: any) => void> = new Map();
  private readonly MONITORING_INTERVAL_MS = 200;
  private readonly MONITORING_TIMEOUT_MS = 30000;
  private readonly UPDATE_DEBOUNCE_MS = 150;
  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {}
  ngAfterViewInit(): void {
    this.initializeScrollbarSync();
  }
  ngOnDestroy(): void {
    this.cleanup();
  }
  /**
   * Starts monitoring for AG Grid initialization and sets up the scrollbars
   */
  private initializeScrollbarSync(): void {
    this.setupUpdateDebouncing();
    this.setupInitializationDebouncing();
    this.setupResetScrollPositionDebouncing();
    this.startGridApiMonitoring();
    this.scheduleMonitoringTimeout();
    // Trigger initialization attempts at specific intervals using RxJS timer
    merge(
      timer(0), // Immediate
      timer(100), // After 100ms
      timer(500), // After 500ms
      timer(1000), // After 1000ms
      timer(2000) // After 2000ms - added longer delay for complex grids
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initializationAttempt$.next();
      });
  }
  /**
   * Sets up debounced updates for scrollbar dimensions
   */
  private setupUpdateDebouncing(): void {
    this.updateScrollbar$
      .pipe(debounceTime(this.UPDATE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateScrollbarDimensions());
  }
  /**
   * Sets up debounced initialization attempts
   */
  private setupInitializationDebouncing(): void {
    this.initializationAttempt$
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.attemptGridInitialization());
  }
  /**
   * Sets up debounced reset scroll position
   */
  private setupResetScrollPositionDebouncing(): void {
    this.resetScrollPosition$
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.executeResetScrollPosition());
  }
  /**
   * Begins monitoring for AG Grid API availability
   */
  private startGridApiMonitoring(): void {
    this.monitoringInterval = window.setInterval(() => {
      this.attemptGridInitialization();
    }, this.MONITORING_INTERVAL_MS);
  }
  /**
   * Sets timeout to stop monitoring after specified duration
   */
  private scheduleMonitoringTimeout(): void {
    setTimeout(() => {
      this.stopMonitoring();
    }, this.MONITORING_TIMEOUT_MS);
  }
  /**
   * Attempts to initialize the grid connection and scrollbar setup
   */
  private attemptGridInitialization(): void {
    if (!this.findScrollbarElements()) {
      return;
    }
    if (!this.findGridApi()) {
      // Try to get grid API from parent component
      this.tryGetGridApiFromParent();
    }
    if (this.gridApi) {
      // Use AG Grid API method
      this.stopMonitoring();
      this.setupScrollbarSynchronization();
      this.setupGridEventListeners();
      this.triggerScrollbarUpdate();
      // Reset scroll position to extreme left on initialization
      this.resetScrollPosition();
    } else {
      // Fallback to DOM-based scrolling
      const hasAgGridDom = this.elementRef.nativeElement?.querySelector('.ag-root-wrapper');
      if (hasAgGridDom) {
        this.stopMonitoring();
        this.setupDomBasedScrollSync();
        // Set up observer to watch for DOM changes and update scrollbar
        this.observeGridChanges();
        // Reset scroll position to extreme left on initialization
        this.resetScrollPosition();
      }
    }
  }
  /**
   * Try to get grid API from parent component context
   */
  private tryGetGridApiFromParent(): void {
    try {
      // Look for miq-ag-table component in parent hierarchy
      let parentElement = this.elementRef.nativeElement.parentElement;
      while (parentElement && !this.gridApi) {
        // Check if parent has an Angular component with getGridApi method
        const ngComponent = (parentElement as any).__ngContext__;
        if (ngComponent && Array.isArray(ngComponent)) {
          for (const context of ngComponent) {
            if (context && typeof context === 'object' && typeof context.getGridApi === 'function') {
              this.gridApi = context.getGridApi();
              if (this.gridApi) break;
            }
          }
        }
        // Check for direct gridApi property
        if ((parentElement as any).gridApi) {
          this.gridApi = (parentElement as any).gridApi;
          break;
        }
        parentElement = parentElement.parentElement;
      }
    } catch (error) {
      // Silently handle errors
    }
  }
  /**
   * Locates required scrollbar DOM elements
   */
  private findScrollbarElements(): boolean {
    const hostElement = this.elementRef.nativeElement;
    this.topScrollbarElement = hostElement?.querySelector('.top-scrollbar') as HTMLElement;
    this.bottomScrollbarElement = hostElement?.querySelector('.bottom-scrollbar') as HTMLElement;
    if (!this.topScrollbarElement && !this.bottomScrollbarElement) return false;
    if (this.topScrollbarElement) {
      this.topScrollbarContent = this.topScrollbarElement.querySelector('.scrollbar-content') as HTMLElement;
    }
    if (this.bottomScrollbarElement) {
      this.bottomScrollbarContent = this.bottomScrollbarElement.querySelector('.scrollbar-content') as HTMLElement;
    }
    return !!(this.topScrollbarContent || this.bottomScrollbarContent);
  }
  /**
   * Locates and extracts AG Grid API from the grid element
   */
  private findGridApi(): boolean {
    const hostElement = this.elementRef.nativeElement;
    // Try multiple ways to find the AG Grid element
    let agGridElement = hostElement?.querySelector('ag-grid-angular') as any;
    if (!agGridElement) {
      // Try finding by component tag
      agGridElement = hostElement?.querySelector('ag-grid') as any;
    }
    if (!agGridElement && hostElement?.tagName?.toLowerCase() === 'ag-grid-angular') {
      // Host element itself might be the ag-grid-angular
      agGridElement = hostElement as any;
    }
    if (!agGridElement) return false;
    try {
      // Try to get API from the ag-grid-angular component in multiple ways
      const possibleApiPaths = [() => agGridElement.componentInstance?.api, () => agGridElement.gridApi];
      for (const getApi of possibleApiPaths) {
        try {
          const api = getApi();
          if (api && typeof api.setHorizontalScrollPosition === 'function') {
            this.gridApi = api;
            return true;
          }
        } catch (error) {
          // Continue to next method
        }
      }
    } catch (error) {}
    return this.tryAlternativeGridApiAccess();
  }
  /**
   * Alternative method to access Grid API through Angular context
   */
  private tryAlternativeGridApiAccess(): boolean {
    try {
      const hostElement = this.elementRef.nativeElement;
      // Try to find the grid API through various possible paths
      const agGridElement = hostElement?.querySelector('ag-grid-angular') as any;
      if (agGridElement) {
        // Check for grid API in component data
        const componentData = agGridElement.__ngContext__ || agGridElement._lView;
        if (componentData) {
          // Search through the component context for gridApi
          for (let i = 0; i < componentData.length; i++) {
            if (componentData[i] && typeof componentData[i] === 'object' && componentData[i].api) {
              this.gridApi = componentData[i].api;
              return true;
            }
          }
        }
      }
      // Last resort: try to find any element with gridApi property
      const allElements = hostElement.querySelectorAll('*');
      for (let j = 0; j < allElements.length; j++) {
        const element = allElements[j];
        if ((element as any).gridApi) {
          this.gridApi = (element as any).gridApi;
          return true;
        }
      }
    } catch (error) {
      // Silently handle access errors
    }
    return false;
  }
  /**
   * Sets up bidirectional scroll synchronization between scrollbars and grid
   */
  private setupScrollbarSynchronization(): void {
    if (!this.gridApi) {
      return;
    }
    // Setup top scrollbar
    if (this.topScrollbarElement) {
      this.topScrollbarScrollHandler = this.handleTopScrollbarScroll.bind(this);
      this.topScrollbarElement.addEventListener('scroll', this.topScrollbarScrollHandler, { passive: true });
    }
    // Setup bottom scrollbar
    if (this.bottomScrollbarElement) {
      this.bottomScrollbarScrollHandler = this.handleBottomScrollbarScroll.bind(this);
      this.bottomScrollbarElement.addEventListener('scroll', this.bottomScrollbarScrollHandler, { passive: true });
    }
  }
  /**
   * Handles top scrollbar scroll events and syncs with grid
   */
  private handleTopScrollbarScroll(): void {
    if (this.isSynchronizingScroll || !this.topScrollbarElement || !this.gridApi) return;
    const scrollLeft = this.topScrollbarElement.scrollLeft;
    this.scheduleHorizontalSync(scrollLeft, 'top');
  }
  /**
   * Handles bottom scrollbar scroll events and syncs with grid
   */
  private handleBottomScrollbarScroll(): void {
    if (this.isSynchronizingScroll || !this.bottomScrollbarElement || !this.gridApi) return;
    const scrollLeft = this.bottomScrollbarElement.scrollLeft;
    this.scheduleHorizontalSync(scrollLeft, 'bottom');
  }
  // Throttled horizontal sync implementation
  private pendingRaf: number | null = null;
  private lastScheduledLeft: number | null = null;
  private lastSource: 'top' | 'bottom' | null = null;
  private scheduleHorizontalSync(left: number, source: 'top' | 'bottom'): void {
    this.lastScheduledLeft = left;
    this.lastSource = source;
    if (this.pendingRaf !== null) return;
    this.pendingRaf = requestAnimationFrame(() => {
      this.pendingRaf = null;
      if (!this.gridApi || this.lastScheduledLeft == null) return;
      this.isSynchronizingScroll = true;
      try {
        this.gridApi.setHorizontalScrollPosition(this.lastScheduledLeft);
        if (this.lastSource === 'top' && this.bottomScrollbarElement) {
          this.bottomScrollbarElement.scrollLeft = this.lastScheduledLeft;
        } else if (this.lastSource === 'bottom' && this.topScrollbarElement) {
          this.topScrollbarElement.scrollLeft = this.lastScheduledLeft;
        }
      } catch (e) {}
      this.isSynchronizingScroll = false;
    });
  }
  /**
   * Registers event listeners for grid changes that affect scrollbar
   */
  private setupGridEventListeners(): void {
    if (!this.gridApi) return;
    const eventsToHandle = [
      'bodyScroll',
      'columnResized',
      'displayedColumnsChanged',
      'gridColumnsChanged',
      'firstDataRendered',
      'gridSizeChanged',
    ];
    eventsToHandle.forEach(eventName => {
      try {
        let handler: (event: any) => void;
        if (eventName === 'bodyScroll') {
          handler = this.handleGridScroll.bind(this);
          this.gridScrollHandler = handler;
        } else {
          handler = () => this.triggerScrollbarUpdate();
        }
        // Store handler reference for cleanup
        this.gridEventHandlers.set(eventName, handler);
        this.gridApi!.addEventListener(eventName, handler);
      } catch (error) {
        // Silently handle event listener registration errors
      }
    });
  }
  /**
   * Handles grid scroll events and syncs with both scrollbars
   */
  private handleGridScroll(event: ScrollEvent): void {
    if (this.isSynchronizingScroll) return;
    if (event.direction === 'horizontal') {
      this.isSynchronizingScroll = true;
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        try {
          const scrollLeft = event.left || 0;
          if (this.topScrollbarElement) {
            this.topScrollbarElement.scrollLeft = scrollLeft;
          }
          if (this.bottomScrollbarElement) {
            this.bottomScrollbarElement.scrollLeft = scrollLeft;
          }
        } catch (error) {
          // Silently handle scroll sync errors
        }
        this.isSynchronizingScroll = false;
      });
    }
  }
  /**
   * Triggers a debounced scrollbar update
   */
  private triggerScrollbarUpdate(): void {
    this.updateScrollbar$.next();
  }
  /**
   * Updates scrollbar dimensions and visibility based on grid content
   */
  private updateScrollbarDimensions(): void {
    if (!this.gridApi) return;
    try {
      const totalColumnsWidth = this.calculateTotalColumnsWidth();
      const containerWidth = this.getGridContainerWidth();
      // Update top scrollbar
      if (this.topScrollbarElement && this.topScrollbarContent) {
        this.setScrollbarContentWidth(this.topScrollbarContent, totalColumnsWidth);
        this.setScrollbarContainerDimensions(this.topScrollbarElement, { width: containerWidth, left: 0 });
        this.setScrollbarVisibility(this.topScrollbarElement, totalColumnsWidth > containerWidth && containerWidth > 0);
      }
      // Update bottom scrollbar
      if (this.bottomScrollbarElement && this.bottomScrollbarContent) {
        this.setScrollbarContentWidth(this.bottomScrollbarContent, totalColumnsWidth);
        this.setScrollbarContainerDimensions(this.bottomScrollbarElement, { width: containerWidth, left: 0 });
        this.setScrollbarVisibility(
          this.bottomScrollbarElement,
          totalColumnsWidth > containerWidth && containerWidth > 0
        );
      }
    } catch (error) {
      // Silently handle update errors
    }
  }
  /**
   * Calculates total width of all displayed columns
   */
  private calculateTotalColumnsWidth(): number {
    const displayedColumns = this.gridApi?.getAllDisplayedColumns() || [];
    return displayedColumns.reduce((totalWidth, column) => {
      return totalWidth + (column.getActualWidth?.() || 0);
    }, 0);
  }
  /**
   * Gets the width of the grid container element
   */
  private getGridContainerWidth(): number {
    const hostElement = this.elementRef.nativeElement;
    const gridContainer =
      hostElement?.querySelector('.ag-root-wrapper') || hostElement?.querySelector('ag-grid-angular');
    return gridContainer?.clientWidth || 0;
  }
  /**
   * Sets the scrollbar container dimensions to match full grid width
   */
  private setScrollbarContainerDimensions(
    scrollbarElement: HTMLElement,
    contentArea: { width: number; left: number }
  ): void {
    if (scrollbarElement) {
      // Set scrollbar to full width to allow scrolling to both extremes
      this.renderer.setStyle(scrollbarElement, 'width', '100%');
      this.renderer.setStyle(scrollbarElement, 'margin-left', '0px');
    }
  }
  /**
   * Sets the width of the scrollbar content element
   */
  private setScrollbarContentWidth(scrollbarContent: HTMLElement, width: number): void {
    if (scrollbarContent) {
      this.renderer.setStyle(scrollbarContent, 'width', `${width}px`);
    }
  }
  /**
   * Controls scrollbar visibility based on content overflow
   */
  private setScrollbarVisibility(scrollbarElement: HTMLElement, shouldShow: boolean): void {
    if (scrollbarElement) {
      this.renderer.setStyle(scrollbarElement, 'display', shouldShow ? 'block' : 'none');
    }
  }
  /**
   * Stops the grid API monitoring interval
   */
  private stopMonitoring(): void {
    if (this.monitoringInterval !== null) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }
  /**
   * Performs cleanup of resources and subscriptions
   */
  private cleanup(): void {
    this.stopMonitoring();
    this.removeEventListeners();
  }
  /**
   * Removes all event listeners
   */
  private removeEventListeners(): void {
    // Remove top scrollbar event listener
    if (this.topScrollbarElement && this.topScrollbarScrollHandler) {
      this.topScrollbarElement.removeEventListener('scroll', this.topScrollbarScrollHandler);
      this.topScrollbarScrollHandler = null;
    }
    // Remove bottom scrollbar event listener
    if (this.bottomScrollbarElement && this.bottomScrollbarScrollHandler) {
      this.bottomScrollbarElement.removeEventListener('scroll', this.bottomScrollbarScrollHandler);
      this.bottomScrollbarScrollHandler = null;
    }
    // Remove grid event listeners
    if (this.gridApi) {
      this.gridEventHandlers.forEach((handler, eventName) => {
        if (eventName !== 'domScrollHandler' && eventName !== 'mutationObserver' && eventName !== 'resizeHandler') {
          try {
            this.gridApi!.removeEventListener(eventName, handler);
          } catch (error) {
            // Silently handle removal errors
          }
        }
      });
    }
    // Handle DOM-based listeners
    this.gridEventHandlers.forEach((handler, eventName) => {
      if (eventName === 'domScrollHandler') {
        // Find and remove DOM scroll listener
        const hostElement = this.elementRef.nativeElement;
        const agBodyViewport = hostElement?.querySelector('.ag-body-viewport') as HTMLElement;
        const agBodyHorizontalScrollViewport = hostElement?.querySelector(
          '.ag-body-horizontal-scroll-viewport'
        ) as HTMLElement;
        const scrollContainer = agBodyHorizontalScrollViewport || agBodyViewport;
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', handler);
        }
      } else if (eventName === 'mutationObserver') {
        // Disconnect mutation observer
        try {
          (handler as any).disconnect();
        } catch (error) {
          // Silently handle disconnect errors
        }
      } else if (eventName === 'resizeHandler') {
        // Remove resize listener
        window.removeEventListener('resize', handler);
      }
    });
    this.gridEventHandlers.clear();
    this.gridScrollHandler = null;
  }
  /**
   * Fallback method to sync scroll using DOM manipulation when AG Grid API is not available
   */
  private setupDomBasedScrollSync(): void {
    // First, detect all possible scroll containers for debugging
    this.detectAllScrollContainers();
    const hostElement = this.elementRef.nativeElement;
    // Try multiple selectors to find the correct scroll container
    const scrollContainerSelectors = [
      '.ag-body-horizontal-scroll-viewport',
      '.ag-body-viewport',
      '.ag-body-horizontal-scroll',
      '.ag-center-cols-viewport',
      '.ag-center-cols-container',
      '.ag-body',
    ];
    let scrollContainer: HTMLElement | null = null;
    for (const selector of scrollContainerSelectors) {
      const candidate = hostElement?.querySelector(selector) as HTMLElement;
      if (candidate && candidate.scrollWidth > candidate.clientWidth) {
        scrollContainer = candidate;
        break;
      }
    }
    if (scrollContainer) {
      // Test manual scroll to verify container is correct
      const originalScrollLeft = scrollContainer.scrollLeft;
      scrollContainer.scrollLeft = 50;
      setTimeout(() => {
        scrollContainer.scrollLeft = originalScrollLeft; // Restore
      }, 100);
      // Setup top scrollbar sync
      if (this.topScrollbarElement) {
        this.topScrollbarScrollHandler = () => {
          if (this.isSynchronizingScroll || !this.topScrollbarElement) {
            return;
          }
          this.isSynchronizingScroll = true;
          requestAnimationFrame(() => {
            try {
              if (scrollContainer && this.topScrollbarElement) {
                const scrollLeft = this.topScrollbarElement.scrollLeft;
                scrollContainer.scrollLeft = scrollLeft;
                // Sync bottom scrollbar
                if (this.bottomScrollbarElement) {
                  this.bottomScrollbarElement.scrollLeft = scrollLeft;
                }
              }
            } catch (error) {
              // Silently handle DOM sync errors
            }
            this.isSynchronizingScroll = false;
          });
        };
        this.topScrollbarElement.addEventListener('scroll', this.topScrollbarScrollHandler, { passive: true });
      }
      // Setup bottom scrollbar sync
      if (this.bottomScrollbarElement) {
        this.bottomScrollbarScrollHandler = () => {
          if (this.isSynchronizingScroll || !this.bottomScrollbarElement) return;
          this.isSynchronizingScroll = true;
          requestAnimationFrame(() => {
            try {
              if (scrollContainer && this.bottomScrollbarElement) {
                const scrollLeft = this.bottomScrollbarElement.scrollLeft;
                scrollContainer.scrollLeft = scrollLeft;
                // Sync top scrollbar
                if (this.topScrollbarElement) {
                  this.topScrollbarElement.scrollLeft = scrollLeft;
                }
              }
            } catch (error) {
              // Silently handle DOM sync errors
            }
            this.isSynchronizingScroll = false;
          });
        };
        this.bottomScrollbarElement.addEventListener('scroll', this.bottomScrollbarScrollHandler, { passive: true });
      }
      // Set up reverse sync (grid to scrollbars)
      const gridScrollHandler = () => {
        if (this.isSynchronizingScroll) return;
        this.isSynchronizingScroll = true;
        requestAnimationFrame(() => {
          try {
            if (scrollContainer) {
              const scrollLeft = scrollContainer.scrollLeft;
              if (this.topScrollbarElement) {
                this.topScrollbarElement.scrollLeft = scrollLeft;
              }
              if (this.bottomScrollbarElement) {
                this.bottomScrollbarElement.scrollLeft = scrollLeft;
              }
            }
          } catch (error) {
            // Silently handle reverse sync errors
          }
          this.isSynchronizingScroll = false;
        });
      };
      scrollContainer.addEventListener('scroll', gridScrollHandler, { passive: true });
      // Store for cleanup
      this.gridEventHandlers.set('domScrollHandler', gridScrollHandler);
      // Update scrollbar dimensions based on DOM
      this.updateScrollbarDimensionsFromDom();
    }
  }
  /**
   * Updates scrollbar dimensions by measuring DOM elements directly
   */
  private updateScrollbarDimensionsFromDom(): void {
    if (
      (!this.topScrollbarElement && !this.bottomScrollbarElement) ||
      (!this.topScrollbarContent && !this.bottomScrollbarContent)
    )
      return;
    try {
      const hostElement = this.elementRef.nativeElement;
      const agHeaderContainer = hostElement?.querySelector('.ag-header-container') as HTMLElement;
      const agBodyContainer = hostElement?.querySelector('.ag-center-cols-container') as HTMLElement;
      const agPinnedLeftContainer = hostElement?.querySelector('.ag-pinned-left-cols-container') as HTMLElement;
      let totalWidth = 0;
      let pinnedLeftWidth = 0;
      // Get pinned left columns width
      if (agPinnedLeftContainer) {
        pinnedLeftWidth = agPinnedLeftContainer.scrollWidth || 0;
      }
      // Get main grid content width
      if (agHeaderContainer) {
        totalWidth = agHeaderContainer.scrollWidth;
      } else if (agBodyContainer) {
        totalWidth = agBodyContainer.scrollWidth;
      }
      // Total scrollable width should include pinned columns + main content
      const fullGridWidth = pinnedLeftWidth + totalWidth;
      const containerWidth = this.getGridContainerWidth();
      if (fullGridWidth > 0) {
        const shouldShow = fullGridWidth > containerWidth && containerWidth > 0;
        // Update top scrollbar
        if (this.topScrollbarElement && this.topScrollbarContent) {
          this.setScrollbarContentWidth(this.topScrollbarContent, fullGridWidth);
          this.setScrollbarContainerDimensions(this.topScrollbarElement, { width: containerWidth, left: 0 });
          this.setScrollbarVisibility(this.topScrollbarElement, shouldShow);
        }
        // Update bottom scrollbar
        if (this.bottomScrollbarElement && this.bottomScrollbarContent) {
          this.setScrollbarContentWidth(this.bottomScrollbarContent, fullGridWidth);
          this.setScrollbarContainerDimensions(this.bottomScrollbarElement, { width: containerWidth, left: 0 });
          this.setScrollbarVisibility(this.bottomScrollbarElement, shouldShow);
        }
      }
    } catch (error) {
      // Silently handle errors
    }
  }
  /**
   * Observes grid DOM changes to update scrollbar when needed
   */
  private observeGridChanges(): void {
    if (!this.elementRef.nativeElement) return;
    try {
      const gridContainer = this.elementRef.nativeElement.querySelector('.ag-root-wrapper');
      if (!gridContainer) return;
      // Use MutationObserver to watch for changes in grid structure
      const observer = new MutationObserver(() => {
        this.updateScrollbarDimensionsFromDom();
      });
      observer.observe(gridContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      // Store observer for cleanup
      this.gridEventHandlers.set('mutationObserver', observer as any);
      // Also listen for resize events
      const resizeHandler = () => {
        setTimeout(() => this.updateScrollbarDimensionsFromDom(), 50);
      };
      window.addEventListener('resize', resizeHandler);
      this.gridEventHandlers.set('resizeHandler', resizeHandler);
    } catch (error) {
      // Silently handle observer setup errors
    }
  }
  /**
   * Public method to manually refresh the scrollbar
   * Can be called from parent components when grid is ready
   */
  public refreshScrollbar(): void {
    this.attemptGridInitialization();
  }
  /**
   * Public method to update scrollbar dimensions
   * Useful when grid data changes
   */
  public updateScrollbar(): void {
    if (this.gridApi) {
      this.triggerScrollbarUpdate();
    } else {
      this.updateScrollbarDimensionsFromDom();
    }
  }
  /**
   * Resets scroll position to the extreme left (position 0)
   * Called during initialization to ensure consistent starting position
   */
  private resetScrollPosition(): void {
    this.resetScrollPosition$.next();
  }
  /**
   * Executes the actual scroll position reset with debounced timing
   */
  private executeResetScrollPosition(): void {
    try {
      if (this.gridApi) {
        // Reset grid scroll position using AG Grid API
        this.gridApi.setHorizontalScrollPosition(0);
      } else {
        // Fallback: reset DOM-based scroll
        const hostElement = this.elementRef.nativeElement;
        const scrollContainer = this.findScrollContainer(hostElement);
        if (scrollContainer) {
          scrollContainer.scrollLeft = 0;
        }
      }
      // Reset custom scrollbars to position 0
      if (this.topScrollbarElement) {
        this.topScrollbarElement.scrollLeft = 0;
      }
      if (this.bottomScrollbarElement) {
        this.bottomScrollbarElement.scrollLeft = 0;
      }
    } catch (error) {
      // Silently handle reset errors
    }
  }
  /**
   * Helper method to find the appropriate scroll container element
   */
  private findScrollContainer(hostElement: HTMLElement): HTMLElement | null {
    const scrollContainerSelectors = [
      '.ag-body-horizontal-scroll-viewport',
      '.ag-body-viewport',
      '.ag-body-horizontal-scroll',
      '.ag-center-cols-viewport',
      '.ag-center-cols-container',
      '.ag-body',
    ];
    for (const selector of scrollContainerSelectors) {
      const candidate = hostElement?.querySelector(selector) as HTMLElement;
      if (candidate && candidate.scrollWidth > candidate.clientWidth) {
        return candidate;
      }
    }
    return null;
  }
  /**
   * Comprehensive detection of all possible scroll containers in AG Grid
   */
  private detectAllScrollContainers(): void {
    const hostElement = this.elementRef.nativeElement;
    const allPossibleSelectors = [
      '.ag-body-horizontal-scroll-viewport',
      '.ag-body-viewport',
      '.ag-body-horizontal-scroll',
      '.ag-center-cols-viewport',
      '.ag-center-cols-container',
      '.ag-body',
      '.ag-root-wrapper',
      '.ag-root',
      '.ag-body-container',
      '[class*="viewport"]',
      '[class*="scroll"]',
    ];
    for (const selector of allPossibleSelectors) {
      const elements = hostElement?.querySelectorAll(selector);
      if (elements && elements.length > 0) {
        elements.forEach((el, index) => {
          const element = el as HTMLElement;
        });
      }
    }
  }
}
